/**
 * Model-catalog derivation + live/static reconciliation.
 *
 * The authoritative catalog is the backend's `GET /v1/models` (rule #1 — the
 * web app reads the same catalog the SDK does). `STATIC_CATALOG`, built from
 * the bundled `models.json`, exists only for instant first paint and as an
 * offline fallback; `ModelCatalogContext` fetches the live catalog and swaps
 * it in. `/v1/models` omits `isOpenSource` and the display `group`, so
 * `catalogFromApi` enriches those two fields from the static file by display
 * name (and maps `owned_by` -> a label for any live-only model).
 *
 * This module imports nothing from `aridel-client`/`config` so it can be used
 * by both without an import cycle.
 */
import modelsData from './models.json';

export interface ModelEntry {
  displayName: string;
  slug?: string;
  group?: string;
  isEnabled?: boolean;
  isOpenSource?: boolean;
  inputCostPer1MTokens?: number;
  outputCostPer1MTokens?: number;
}

export interface Catalog {
  proprietaryModels: string[];
  openSourceModels: string[];
  allModels: string[];
  providerGroups: Array<{ label: string; models: string[] }>;
  modelPricing: Record<string, { input: number; output: number }>;
  modelTier: Record<string, 'Light' | 'Standard' | 'Premium'>;
  /** Public wire id (slug) -> display name. The API echoes slugs in
   *  `model` fields since the harness-compat change; the UI renders names. */
  slugToName: Record<string, string>;
  otherGroupLabel: string;
}

export const OTHER_GROUP_LABEL = 'Other';

/** Derive the grouped/priced/tiered structures the selectors render. */
export function deriveCatalog(models: ModelEntry[]): Catalog {
  const enabled = models.filter((m) => m.isEnabled !== false);

  const proprietaryModels = enabled.filter((m) => !m.isOpenSource).map((m) => m.displayName);
  const openSourceModels = enabled.filter((m) => m.isOpenSource).map((m) => m.displayName);
  const allModels = enabled.map((m) => m.displayName);

  const groupOrder: string[] = [];
  const groupMap: Record<string, string[]> = {};
  for (const m of enabled) {
    const group = m.group ?? OTHER_GROUP_LABEL;
    if (!groupMap[group]) {
      groupMap[group] = [];
      groupOrder.push(group);
    }
    groupMap[group].push(m.displayName);
  }
  const providerGroups = groupOrder
    .filter((g) => g !== OTHER_GROUP_LABEL)
    .map((g) => ({ label: g, models: groupMap[g] }));

  const modelPricing: Record<string, { input: number; output: number }> = Object.fromEntries(
    enabled.map((m) => [
      m.displayName,
      { input: m.inputCostPer1MTokens ?? 0, output: m.outputCostPer1MTokens ?? 0 },
    ]),
  );

  const modelTier: Record<string, 'Light' | 'Standard' | 'Premium'> = Object.fromEntries(
    enabled.map((m) => {
      const out = m.outputCostPer1MTokens ?? 0;
      const tier: 'Light' | 'Standard' | 'Premium' =
        out < 1.0 ? 'Light' : out < 10.0 ? 'Standard' : 'Premium';
      return [m.displayName, tier];
    }),
  );

  const slugToName: Record<string, string> = Object.fromEntries(
    enabled.filter((m) => m.slug).map((m) => [m.slug as string, m.displayName]),
  );

  return {
    proprietaryModels,
    openSourceModels,
    allModels,
    providerGroups,
    modelPricing,
    modelTier,
    slugToName,
    otherGroupLabel: OTHER_GROUP_LABEL,
  };
}

const STATIC_MODELS = (modelsData as { models: ModelEntry[] }).models;
const STATIC_BY_NAME: Record<string, ModelEntry> = Object.fromEntries(
  STATIC_MODELS.map((m) => [m.displayName, m]),
);

/** First-paint / offline fallback, derived from the bundled models.json. */
export const STATIC_CATALOG: Catalog = deriveCatalog(STATIC_MODELS);

/** Subset of a `GET /v1/models` entry that we consume. */
export interface ApiModel {
  id: string;
  name?: string;
  owned_by?: string;
  pricing?: { prompt?: string; completion?: string };
}

// Provider id (owned_by) -> display label, for live models absent from the
// static file. The static `group` wins when present.
const PROVIDER_LABEL: Record<string, string> = {
  anthropic: 'Anthropic',
  openai: 'OpenAI',
  google: 'Google',
  deepseek: 'DeepSeek',
  qwen3: 'Qwen',
  glm: 'GLM',
  kimi: 'Kimi',
  minimax: 'MiniMax',
  nova: 'Amazon Nova',
};

/** Build a catalog from the live `/v1/models` payload, enriching the fields
 *  that endpoint doesn't return (`isOpenSource`, display `group`). */
export function catalogFromApi(apiModels: ApiModel[]): Catalog {
  const entries: ModelEntry[] = apiModels
    .filter((m) => m.id && m.id !== 'auto' && m.owned_by !== 'aridel')
    .map((m) => {
      // Live ids are slugs; `name` is the display name (older deploys sent
      // the display name as `id` and no `name` — tolerate both).
      const displayName = m.name ?? m.id;
      const stat = STATIC_BY_NAME[displayName];
      return {
        displayName,
        slug: m.id,
        group: stat?.group ?? PROVIDER_LABEL[m.owned_by ?? ''] ?? (m.owned_by || OTHER_GROUP_LABEL),
        isEnabled: true, // /v1/models only returns enabled models
        isOpenSource: stat?.isOpenSource ?? false,
        inputCostPer1MTokens: parseFloat(m.pricing?.prompt ?? '0') || 0,
        outputCostPer1MTokens: parseFloat(m.pricing?.completion ?? '0') || 0,
      };
    });
  return deriveCatalog(entries);
}
